import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion'
import {
  CalendarDays,
  Tent,
  Wind,
  Users,
  MessagesSquare,
  Building2,
  ArrowRight,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { BlurFade } from '@/components/ui/blur-fade'

const OFFERINGS = [
  {
    Icon: CalendarDays,
    title: 'Discover events & classes',
    tag: 'In-person & online',
    body: 'Browse curated yoga, breathwork, meditation, sound, and movement sessions happening across Bangalore and online — and book your spot in a tap.',
    to: '/events',
    cta: 'Explore experiences',
  },
  {
    Icon: Tent,
    title: 'Retreats & immersives',
    tag: 'Weekends & beyond',
    body: 'Step further out with weekend retreats and immersive journeys designed to help you truly slow down, reset, and return a little changed.',
    to: '/events',
    cta: 'Find a retreat',
  },
  {
    Icon: Wind,
    title: 'Daily resets & practices',
    tag: '5–15 minutes',
    body: 'Short breathwork and reset sessions you can return to between everything else — the small, repeatable rhythms that keep the loop going.',
    to: '/mid-day-reset',
    cta: 'Try a daily reset',
  },
  {
    Icon: Users,
    title: 'Trusted facilitators & spaces',
    tag: '40+ practitioners',
    body: 'Explore the people behind the practice. Find facilitators and wellness spaces whose work genuinely resonates with where you are.',
    to: '/facilitators',
    cta: 'Meet the facilitators',
  },
  {
    Icon: MessagesSquare,
    title: 'Community & circles',
    tag: 'Always on',
    body: 'Join WhatsApp circles, weekly check-ins, and gatherings — a community that gently keeps you coming back, so you’re never doing this alone.',
    to: '/onboarding',
    cta: 'Join the community',
  },
  {
    Icon: Building2,
    title: 'Workplace & founder programs',
    tag: 'For teams',
    body: 'Structured programs like Energizers bring breathwork, movement, emotional agility, and recovery to founders, leaders, and high-pressure teams.',
    to: '/corporates',
    cta: 'Explore workplace wellness',
  },
]

const AUTO_ADVANCE_MS = 5000

export function OfferingsSection() {
  const reduce = useReducedMotion()
  const [active, setActive] = useState(0)
  const [paused, setPaused] = useState(false)

  // Gently cycle through the offerings so the section feels alive. Pauses on
  // hover/focus and is disabled entirely for reduced-motion users.
  useEffect(() => {
    if (reduce || paused) return undefined
    const id = setInterval(() => {
      setActive((i) => (i + 1) % OFFERINGS.length)
    }, AUTO_ADVANCE_MS)
    return () => clearInterval(id)
  }, [reduce, paused])

  const activeOffering = OFFERINGS[active]
  const { Icon } = activeOffering

  return (
    <section className="relative overflow-hidden bg-primary-800 text-cream">
      {/* soft orbiting glow — purely decorative */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -right-32 top-1/2 h-[36rem] w-[36rem] -translate-y-1/2 rounded-full bg-primary-500/20 blur-3xl"
      />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -left-40 -top-40 h-[28rem] w-[28rem] rounded-full bg-primary-400/10 blur-3xl"
      />

      <div className="relative mx-auto max-w-[1200px] px-6 py-20 lg:px-20 lg:py-24">
        <BlurFade>
          <span className="inline-block rounded-full border border-cream/20 bg-cream/5 px-4 py-1.5 text-xs font-semibold uppercase tracking-wide text-primary-200">
            What you’ll find here
          </span>
          <h2 className="mt-6 max-w-2xl text-3xl font-bold leading-tight sm:text-4xl lg:text-5xl">
            Everything for your wellbeing, <br className="hidden sm:block" />
            in one loop.
          </h2>
          <p className="mt-4 max-w-xl text-lg leading-relaxed text-cream/70">
            From a single class to a daily rhythm — discover the experiences,
            people, and programs that help you start where you are and keep
            coming back.
          </p>
        </BlurFade>

        <div
          className="mt-14 grid gap-8 lg:grid-cols-[42fr_58fr] lg:gap-12"
          role="tablist"
          aria-label="What you can find on Wellness Loop"
          onMouseEnter={() => setPaused(true)}
          onMouseLeave={() => setPaused(false)}
          onFocusCapture={() => setPaused(true)}
          onBlurCapture={() => setPaused(false)}
        >
          {/* Left — selectable list */}
          <div className="flex flex-col">
            {OFFERINGS.map((offering, i) => {
              const isActive = i === active
              return (
                <button
                  key={offering.title}
                  type="button"
                  role="tab"
                  id={`offering-tab-${i}`}
                  aria-selected={isActive}
                  aria-controls="offering-panel"
                  onClick={() => setActive(i)}
                  onMouseEnter={() => setActive(i)}
                  className={cn(
                    'group relative flex items-center gap-4 border-l-2 py-4 pl-5 pr-3 text-left transition-colors duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-300',
                    isActive
                      ? 'border-primary-300'
                      : 'border-cream/10 hover:border-cream/30'
                  )}
                >
                  <span
                    className={cn(
                      'font-mono text-sm tabular-nums transition-colors duration-300',
                      isActive ? 'text-primary-300' : 'text-cream/40'
                    )}
                  >
                    {String(i + 1).padStart(2, '0')}
                  </span>
                  <span
                    className={cn(
                      'text-lg font-semibold transition-colors duration-300 sm:text-xl',
                      isActive ? 'text-cream' : 'text-cream/55 group-hover:text-cream/80'
                    )}
                  >
                    {offering.title}
                  </span>

                  {/* auto-advance progress bar under the active item */}
                  {isActive && !reduce && !paused && (
                    <motion.span
                      key={`progress-${i}`}
                      aria-hidden="true"
                      className="absolute -left-0.5 bottom-0 top-0 w-0.5 origin-top bg-primary-300"
                      initial={{ scaleY: 0 }}
                      animate={{ scaleY: 1 }}
                      transition={{ duration: AUTO_ADVANCE_MS / 1000, ease: 'linear' }}
                    />
                  )}
                </button>
              )
            })}
          </div>

          {/* Right — spotlight panel */}
          <div
            id="offering-panel"
            role="tabpanel"
            aria-labelledby={`offering-tab-${active}`}
            className="relative min-h-[20rem] overflow-hidden rounded-3xl border border-cream/10 bg-cream/[0.04] p-8 backdrop-blur-sm sm:p-10 lg:min-h-[22rem]"
          >
            <AnimatePresence mode="wait">
              <motion.div
                key={active}
                initial={reduce ? false : { opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                exit={reduce ? { opacity: 0 } : { opacity: 0, y: -16 }}
                transition={{ duration: 0.35, ease: 'easeOut' }}
                className="flex h-full flex-col"
              >
                <div className="flex items-center gap-4">
                  <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-primary-500/20 text-primary-200">
                    <Icon className="h-7 w-7" strokeWidth={1.5} />
                  </span>
                  <span className="rounded-full bg-primary-500/25 px-3 py-1 text-sm font-semibold text-primary-100">
                    {activeOffering.tag}
                  </span>
                </div>

                <h3 className="mt-7 text-2xl font-bold text-cream sm:text-3xl">
                  {activeOffering.title}
                </h3>
                <p className="mt-4 max-w-xl text-lg leading-relaxed text-cream/75">
                  {activeOffering.body}
                </p>

                <Link
                  to={activeOffering.to}
                  className="mt-auto inline-flex w-fit items-center gap-2 rounded-full bg-cream px-6 py-3 text-base font-semibold text-primary-900 shadow-lg transition-colors hover:bg-cream-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-300 focus-visible:ring-offset-2 focus-visible:ring-offset-primary-800"
                >
                  {activeOffering.cta}
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </div>
    </section>
  )
}
